// src/components/opportunity/pdf/generateFormPdf.ts

import type { OrgFormFilledBlock } from "@/lib/opportunity/orgFormInstanceTypes";
import type { ProposalContactInfo } from "@/lib/opportunity/proposalContactTypes";
import { uploadGeneratedOrgFormPdf } from "@/lib/opportunity/orgFormGeneratedClient";
import { resolveLogoSrc, revokeIfBlobUrl } from "./pdfLogo";
import { pdfRenderErrorMessage } from "./pdfChunkLoadError";

export type GenerateFormPdfArgs = {
  formInstanceId: string;
  formTitle: string;
  brandName: string;
  tagline: string | null;
  logoUrl: string | null;
  blocks: OrgFormFilledBlock[];
  includeCalculations?: boolean;
  senderContact?: ProposalContactInfo | null;
};

export type RenderFormPdfResult = { ok: true; blob: Blob } | { ok: false; error: string };

export type GenerateFormPdfResult =
  | { ok: true; outputId: string }
  | { ok: false; error: string };

export async function renderFormPdfToBlob(args: GenerateFormPdfArgs): Promise<RenderFormPdfResult> {
  let logoSrc: string | null = null;
  try {
    logoSrc = await resolveLogoSrc(args.logoUrl);
    const [{ pdf }, { OrgFormPdf }] = await Promise.all([
      import("@react-pdf/renderer"),
      import("./OrgFormPdf"),
    ]);

    const document = OrgFormPdf({
      brandName: args.brandName,
      tagline: args.tagline,
      logoSrc,
      formTitle: args.formTitle,
      generatedAt: new Date(),
      blocks: args.blocks,
      includeCalculations: args.includeCalculations === true,
      senderContact: args.senderContact ?? null,
    });
    const blob = await pdf(document).toBlob();
    return { ok: true, blob };
  } catch (err) {
    return { ok: false, error: pdfRenderErrorMessage(err) };
  } finally {
    if (logoSrc) revokeIfBlobUrl(logoSrc);
  }
}

export async function uploadRenderedFormPdf(args: {
  formInstanceId: string;
  blob: Blob;
}): Promise<GenerateFormPdfResult> {
  const uploaded = await uploadGeneratedOrgFormPdf({
    formInstanceId: args.formInstanceId,
    blob: args.blob,
  });
  if (!uploaded.ok) return { ok: false, error: uploaded.error };
  return { ok: true, outputId: uploaded.output.id };
}

export async function generateAndUploadFormPdf(
  args: GenerateFormPdfArgs,
): Promise<GenerateFormPdfResult> {
  const rendered = await renderFormPdfToBlob(args);
  if (!rendered.ok) return { ok: false, error: rendered.error };
  return uploadRenderedFormPdf({ formInstanceId: args.formInstanceId, blob: rendered.blob });
}
